// Thin repository layer over Dexie tables: persistence + domain queries. UI and
// services depend on these, not on Dexie directly, so storage stays swappable.
import { OmrDb } from './schema.js';
import type {
  ClassRecord,
  StudentRecord,
  QuizRecord,
  QuizAssignmentRecord,
  AnswerKeyRecord,
  ResultRecord,
  Preset,
  IdentificationMode,
  TestifyExamRecord,
  CompetencyMapRecord,
  CompetencyRecord,
  SettingsRecord,
} from './schema.js';
import { EMPTY_SETTINGS } from './schema.js';

export const newId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'id-' + Math.abs(hashString(String(Date.now()) + ':' + counter())).toString(36);

let _c = 0;
const counter = () => ++_c;
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

export class Repositories {
  constructor(private db: OmrDb) {}

  // ---- classes ----------------------------------------------------------
  async createClass(input: Omit<ClassRecord, 'id' | 'createdAt'>): Promise<ClassRecord> {
    const rec: ClassRecord = { id: newId(), createdAt: Date.now(), ...input };
    await this.db.classes.add(rec);
    return rec;
  }
  listClasses(): Promise<ClassRecord[]> {
    return this.db.classes.orderBy('name').toArray();
  }
  getClass(id: string): Promise<ClassRecord | undefined> {
    return this.db.classes.get(id);
  }
  async updateClass(id: string, patch: Partial<Omit<ClassRecord, 'id' | 'createdAt'>>): Promise<void> {
    await this.db.classes.update(id, patch);
  }
  /** What a deleteClass would remove — for the confirm prompt. Quizzes are shared
   *  across classes, so they are NOT counted; only this class's runs of them. */
  async classDeletionImpact(id: string): Promise<{ students: number; assignments: number; results: number }> {
    const [students, assignments] = await Promise.all([
      this.db.students.where('classId').equals(id).count(),
      this.db.quizAssignments.where('classId').equals(id).toArray(),
    ]);
    const results = assignments.length
      ? await this.db.results.where('quizAssignmentId').anyOf(assignments.map((a) => a.id)).count()
      : 0;
    return { students, assignments: assignments.length, results };
  }
  /** Delete a class and everything scoped to it: its students, its runs of any quiz
   *  (QuizAssignments) and the scanned results in those runs. Irreversible. The
   *  quizzes themselves are shared across classes and are left intact. */
  async deleteClass(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.classes, this.db.students, this.db.quizAssignments, this.db.results, async () => {
      const asgIds = (await this.db.quizAssignments.where('classId').equals(id).toArray()).map((a) => a.id);
      if (asgIds.length) await this.db.results.where('quizAssignmentId').anyOf(asgIds).delete();
      await this.db.quizAssignments.where('classId').equals(id).delete();
      await this.db.students.where('classId').equals(id).delete();
      await this.db.classes.delete(id);
    });
  }

  // ---- students (identified by 12-digit LRN) ---------------------------
  studentsInClass(classId: string): Promise<StudentRecord[]> {
    return this.db.students.where('classId').equals(classId).sortBy('name');
  }
  /** Every student across all classes (for the global Students roster). */
  allStudents(): Promise<StudentRecord[]> {
    return this.db.students.orderBy('name').toArray();
  }
  getStudent(id: string): Promise<StudentRecord | undefined> {
    return this.db.students.get(id);
  }
  /** Add a student; LRN must be 12 digits and unique within the class. */
  async addStudent(input: Omit<StudentRecord, 'id'>): Promise<StudentRecord> {
    const lrn = input.lrn.trim();
    await this.assertLrnFree(input.classId, lrn);
    const rec: StudentRecord = { id: newId(), classId: input.classId, lrn, name: input.name, sex: input.sex };
    await this.db.students.add(rec);
    return rec;
  }
  /** Bulk enrolment (CSV import). Each row's LRN must be 12 digits, unique in class. */
  async addStudents(
    classId: string,
    rows: Array<{ lrn: string; name: string; sex?: 'M' | 'F' | '' }>,
  ): Promise<StudentRecord[]> {
    const used = new Set((await this.studentsInClass(classId)).map((s) => s.lrn));
    const recs: StudentRecord[] = [];
    for (const r of rows) {
      const lrn = r.lrn.trim();
      if (used.has(lrn)) throw new Error(`LRN ${lrn} already in this class`);
      used.add(lrn);
      recs.push({ id: newId(), classId, lrn, name: r.name, sex: r.sex });
    }
    await this.db.students.bulkAdd(recs);
    return recs;
  }
  async updateStudent(id: string, patch: Partial<Omit<StudentRecord, 'id' | 'classId'>>): Promise<void> {
    if (patch.lrn !== undefined) {
      const s = await this.db.students.get(id);
      if (s) await this.assertLrnFree(s.classId, patch.lrn.trim(), id);
    }
    await this.db.students.update(id, patch);
  }
  async deleteStudent(id: string): Promise<void> {
    await this.db.students.delete(id);
  }
  /** Match a scanned LRN to a roster student within the assignment's class. */
  findByLrn(classId: string, lrn: string): Promise<StudentRecord | undefined> {
    return this.db.students.where('[classId+lrn]').equals([classId, lrn]).first();
  }
  private async assertLrnFree(classId: string, lrn: string, exceptId?: string) {
    if (!/^\d{12}$/.test(lrn)) throw new Error(`LRN must be exactly 12 digits: "${lrn}"`);
    const clash = await this.findByLrn(classId, lrn);
    if (clash && clash.id !== exceptId) throw new Error(`LRN ${lrn} already used by ${clash.name}`);
  }

  // ---- quizzes + assignments -------------------------------------------
  /** Create a quiz (decoupled from class) and assign it to one or more classes. */
  async createQuiz(
    input: Omit<QuizRecord, 'id' | 'createdAt'>,
    assign: Array<{ classId: string; dateAdministered: string; identificationMode?: IdentificationMode }>,
  ): Promise<{ quiz: QuizRecord; assignments: QuizAssignmentRecord[] }> {
    const quiz: QuizRecord = { id: newId(), createdAt: Date.now(), ...input };
    // One assignment per class (brief §DATA MODEL): collapse a repeated class so
    // the quiz can never start out with two runs of the same class.
    const seen = new Set<string>();
    assign = assign.filter((a) => !seen.has(a.classId) && seen.add(a.classId));
    const assignments: QuizAssignmentRecord[] = assign.map((a) => ({
      id: newId(),
      quizId: quiz.id,
      classId: a.classId,
      dateAdministered: a.dateAdministered,
      identificationMode: a.identificationMode ?? 'roster',
      createdAt: Date.now(),
    }));
    await this.db.transaction('rw', this.db.quizzes, this.db.quizAssignments, async () => {
      await this.db.quizzes.add(quiz);
      if (assignments.length) await this.db.quizAssignments.bulkAdd(assignments);
    });
    return { quiz, assignments };
  }
  getQuiz(id: string): Promise<QuizRecord | undefined> {
    return this.db.quizzes.get(id);
  }
  listQuizzes(): Promise<QuizRecord[]> {
    return this.db.quizzes.orderBy('createdAt').reverse().toArray();
  }
  assignmentsForQuiz(quizId: string): Promise<QuizAssignmentRecord[]> {
    return this.db.quizAssignments.where('quizId').equals(quizId).toArray();
  }
  getAssignment(id: string): Promise<QuizAssignmentRecord | undefined> {
    return this.db.quizAssignments.get(id);
  }
  /** This quiz's run of one class, if it already has one. */
  assignmentFor(quizId: string, classId: string): Promise<QuizAssignmentRecord | undefined> {
    return this.db.quizAssignments.where('[quizId+classId]').equals([quizId, classId]).first();
  }
  /** Assign a class to a quiz. One assignment per class: assigning a class that
   *  already has a run throws instead of creating a duplicate (which would split
   *  the class's scanned papers across two rows and double-count the analysis). */
  async addAssignment(quizId: string, classId: string, dateAdministered: string, identificationMode: IdentificationMode = 'roster', customFields?: Record<string, string>): Promise<QuizAssignmentRecord> {
    if (await this.assignmentFor(quizId, classId)) throw new Error('That class is already assigned to this quiz');
    const rec: QuizAssignmentRecord = { id: newId(), quizId, classId, dateAdministered, identificationMode, customFields, createdAt: Date.now() };
    await this.db.quizAssignments.add(rec);
    return rec;
  }
  async updateAssignment(id: string, patch: Partial<Omit<QuizAssignmentRecord, 'id'>>): Promise<void> {
    await this.db.quizAssignments.update(id, patch);
  }
  /** What removing one assigned class would delete — for the confirm prompt.
   *  `synced` results already reached TESTIFY; deleting them here does NOT undo
   *  that upload, so the prompt has to say so. */
  async assignmentDeletionImpact(id: string): Promise<{ results: number; synced: number }> {
    const rows = await this.db.results.where('quizAssignmentId').equals(id).toArray();
    return { results: rows.length, synced: rows.filter((r) => r.syncStatus === 'synced').length };
  }
  /** Unassign a class from a quiz: drops that run and the papers scanned in it.
   *  The quiz, its key and every other class's run are untouched. Irreversible. */
  async deleteAssignment(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.quizAssignments, this.db.results, async () => {
      await this.db.results.where('quizAssignmentId').equals(id).delete();
      await this.db.quizAssignments.delete(id);
    });
  }
  /** Delete a quiz and everything scoped to it: its assignments, answer key,
   *  competency map and every scanned result. Irreversible. A TESTIFY exam record
   *  is left alone — sibling subject quizzes share it. */
  async deleteQuiz(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.quizzes, this.db.quizAssignments, this.db.answerKeys, this.db.competencyMaps, this.db.results, async () => {
      const asgIds = (await this.db.quizAssignments.where('quizId').equals(id).toArray()).map((a) => a.id);
      if (asgIds.length) await this.db.results.where('quizAssignmentId').anyOf(asgIds).delete();
      await this.db.quizAssignments.where('quizId').equals(id).delete();
      await this.db.answerKeys.where('quizId').equals(id).delete();
      await this.db.competencyMaps.where('quizId').equals(id).delete();
      await this.db.quizzes.delete(id);
    });
  }

  // ---- answer key (one per quiz) ---------------------------------------
  /** Upsert a quiz's key. `extra` carries a TESTIFY hashed/locked key (§13). */
  async setAnswerKey(
    quizId: string,
    answers: Record<number, string>,
    points?: Record<number, number>,
    extra?: {
      hashedAnswers?: Record<number, Record<string, string>>;
      locked?: boolean;
      uploadMap?: Record<number, { quesId: number; choiceIds: Record<string, number> }>;
    },
  ): Promise<AnswerKeyRecord> {
    const existing = await this.answerKeyFor(quizId);
    const rec: AnswerKeyRecord = { id: existing?.id ?? newId(), quizId, answers, points, ...extra };
    await this.db.answerKeys.put(rec);
    return rec;
  }
  async answerKeyFor(quizId: string): Promise<AnswerKeyRecord | undefined> {
    return this.db.answerKeys.where('quizId').equals(quizId).first();
  }

  // ---- competency map / Table of Specifications (one per quiz) -----------
  competencyMapFor(quizId: string): Promise<CompetencyMapRecord | undefined> {
    return this.db.competencyMaps.where('quizId').equals(quizId).first();
  }
  /** Upsert a quiz's TOS. Replaces the whole competency list — the editor always
   *  hands back the full set, so there's no partial-merge ambiguity. */
  async setCompetencyMap(
    quizId: string,
    competencies: CompetencyRecord[],
    opts: { origin?: CompetencyMapRecord['origin'] } = {},
  ): Promise<CompetencyMapRecord> {
    const existing = await this.competencyMapFor(quizId);
    const rec: CompetencyMapRecord = {
      id: existing?.id ?? newId(),
      quizId,
      competencies,
      origin: opts.origin ?? existing?.origin ?? 'local',
      updatedAt: Date.now(),
    };
    await this.db.competencyMaps.put(rec);
    return rec;
  }
  async deleteCompetencyMap(quizId: string): Promise<void> {
    await this.db.competencyMaps.where('quizId').equals(quizId).delete();
  }

  // ---- settings (school / teacher, one row) ------------------------------
  /** Never null: an unfilled settings row reads as EMPTY_SETTINGS, so report
   *  builders can always ask for it without a null branch. */
  async getSettings(): Promise<SettingsRecord> {
    return (await this.db.settings.get('app')) ?? { ...EMPTY_SETTINGS };
  }
  async saveSettings(patch: Partial<Omit<SettingsRecord, 'id' | 'updatedAt'>>): Promise<SettingsRecord> {
    const rec: SettingsRecord = { ...(await this.getSettings()), ...patch, id: 'app', updatedAt: Date.now() };
    await this.db.settings.put(rec);
    return rec;
  }

  // ---- TESTIFY exams (§13) ---------------------------------------------
  async addTestifyExam(rec: TestifyExamRecord): Promise<TestifyExamRecord> {
    await this.db.testifyExams.put(rec);
    return rec;
  }
  getTestifyExam(id: string): Promise<TestifyExamRecord | undefined> {
    return this.db.testifyExams.get(id);
  }
  listTestifyExams(): Promise<TestifyExamRecord[]> {
    return this.db.testifyExams.toArray();
  }
  /** The subject quizzes imported from one TESTIFY exam (a unified exam's parts). */
  quizzesForTestifyExam(examId: string): Promise<QuizRecord[]> {
    return this.db.quizzes.where('testifyExamId').equals(examId).toArray();
  }

  // ---- results (scoped to an assignment) -------------------------------
  async saveResult(rec: ResultRecord): Promise<ResultRecord> {
    await this.db.results.put(rec);
    return rec;
  }
  getResult(id: string): Promise<ResultRecord | undefined> {
    return this.db.results.get(id);
  }
  resultsForAssignment(quizAssignmentId: string): Promise<ResultRecord[]> {
    return this.db.results.where('quizAssignmentId').equals(quizAssignmentId).toArray();
  }
  /** Every result matched to a roster student (their cross-quiz history). */
  resultsForStudent(studentId: string): Promise<ResultRecord[]> {
    return this.db.results.where('studentId').equals(studentId).toArray();
  }
  /** All results across every class that ran this quiz (rolled-up view). */
  async resultsForQuiz(quizId: string): Promise<ResultRecord[]> {
    const asgs = await this.assignmentsForQuiz(quizId);
    if (!asgs.length) return [];
    return this.db.results.where('quizAssignmentId').anyOf(asgs.map((a) => a.id)).toArray();
  }
  async deleteResult(id: string): Promise<void> {
    await this.db.results.delete(id);
  }

  // ---- TESTIFY upload outbox (§13.8) -----------------------------------
  /** Results queued for TESTIFY upload (syncStatus 'pending' or a prior 'rejected'
   *  the teacher chose to retry). Scoped to one assignment. */
  async uploadableResults(quizAssignmentId: string): Promise<ResultRecord[]> {
    const rows = await this.db.results.where('quizAssignmentId').equals(quizAssignmentId).toArray();
    return rows.filter((r) => r.syncStatus === 'pending' || r.syncStatus === 'rejected');
  }
  /** Count results by sync state for an assignment (drives the sync panel). */
  async syncCounts(quizAssignmentId: string): Promise<Record<NonNullable<ResultRecord['syncStatus']>, number>> {
    const rows = await this.db.results.where('quizAssignmentId').equals(quizAssignmentId).toArray();
    const acc = { local_only: 0, pending: 0, synced: 0, rejected: 0 };
    for (const r of rows) acc[r.syncStatus ?? 'local_only']++;
    return acc;
  }
  /** Record the outcome of an upload attempt on one result. */
  async setSyncStatus(id: string, status: ResultRecord['syncStatus'], error?: string): Promise<void> {
    await this.db.results.update(id, { syncStatus: status, syncError: error });
  }
}
